import React from "react";
import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import { buildWhatsAppLink } from "@/lib/site";

const tiers = [
  {
    days: "7",
    label: "A Soft Reset",
    description: "A short, restorative stay for early recovery and orientation.",
    nonAc: "₹15,000",
    ac: "₹20,000",
  },
  {
    days: "14",
    label: "Foundation Stay",
    description: "A focused fortnight of rebuilding rhythm, sleep, and strength.",
    nonAc: "₹30,000",
    ac: "₹40,000",
  },
  {
    days: "21",
    label: "Full Sequence",
    description: "Our most chosen package, the classical Prasava Raksha unfolds in full.",
    nonAc: "₹44,500",
    ac: "₹59,000",
    featured: true,
  },
  {
    days: "28",
    label: "Deep Recovery",
    description: "An extended arc for deeper tissue healing and confidence with the baby.",
    nonAc: "₹59,000",
    ac: "₹78,000",
  },
  {
    days: "42",
    label: "Traditional 42",
    description: "The classical sutika kala, the complete postnatal window.",
    nonAc: "₹88,500",
    ac: "₹117,000",
  },
];

export const Pricing = () => {
  const [ac, setAc] = React.useState(false);

  return (
    <section
      id="pricing"
      className="py-16 md:py-36 bg-ivory"
      data-testid="pricing-section"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-xl"
          >
            <div className="text-xs uppercase tracking-[0.22em] text-sage font-medium">
              Stay Packages
            </div>
            <h2 className="mt-5 font-serif text-3xl sm:text-4xl lg:text-5xl text-charcoal leading-tight tracking-tight">
              Choose the length of your <span className="italic text-sage-700">healing</span>.
            </h2>
            <p className="mt-5 text-base md:text-lg text-charcoal/65 leading-relaxed">
              All packages include the villa, meals, treatments, doctor visits
              and bystander accommodation. Prices are per stay, all-inclusive.
            </p>
          </motion.div>

          <div
            className="self-start md:self-auto inline-flex items-center p-1 rounded-full border border-sage/30 bg-white"
            role="tablist"
            data-testid="pricing-toggle"
          >
            <button
              type="button"
              role="tab"
              aria-selected={!ac}
              onClick={() => setAc(false)}
              className={`px-5 py-2.5 rounded-full text-sm font-medium transition-colors ${
                !ac ? "bg-sage text-ivory" : "text-charcoal/70 hover:text-charcoal"
              }`}
              data-testid="pricing-toggle-nonac"
            >
              Non-AC
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={ac}
              onClick={() => setAc(true)}
              className={`px-5 py-2.5 rounded-full text-sm font-medium transition-colors ${
                ac ? "bg-sage text-ivory" : "text-charcoal/70 hover:text-charcoal"
              }`}
              data-testid="pricing-toggle-ac"
            >
              AC
            </button>
          </div>
        </div>

        <div className="mt-14">
          {/* Mobile: vertical stack, featured 21-day card moved to top for conversion */}
          <div
            className="md:hidden pt-5 flex flex-col gap-4 pb-4"
            data-testid="pricing-mobile-stack"
          >
            {[...tiers].sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0)).map((t, i) => (
              <motion.article
                key={t.days}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: i * 0.04, ease: [0.22, 1, 0.36, 1] }}
                className={`relative w-full flex flex-col p-6 rounded-3xl border ${
                  t.featured
                    ? "bg-charcoal text-ivory border-charcoal shadow-[0_20px_60px_-20px_rgba(30,30,30,0.35)]"
                    : "bg-white text-charcoal border-sage/15 shadow-[0_2px_10px_-4px_rgba(30,30,30,0.04)]"
                }`}
                data-testid={`pricing-package-mobile-${t.days}`}
              >
                {t.featured && (
                  <span className="absolute -top-3 left-6 px-3 py-1 rounded-full bg-sage text-ivory text-[10px] uppercase tracking-[0.18em]">
                    Most Chosen
                  </span>
                )}
                <div className={`text-xs uppercase tracking-[0.2em] ${t.featured ? "text-ivory/65" : "text-sage"}`}>
                  {t.days} Days
                </div>
                <div className={`mt-2 font-serif text-2xl ${t.featured ? "text-ivory" : "text-charcoal"}`}>
                  {t.label}
                </div>
                <p className={`mt-2 text-sm leading-relaxed ${t.featured ? "text-ivory/70" : "text-charcoal/60"}`}>
                  {t.description}
                </p>
                <div className="mt-5">
                  <div className={`font-serif text-3xl tracking-tight ${t.featured ? "text-ivory" : "text-charcoal"}`}>
                    {ac ? t.ac : t.nonAc}
                  </div>
                  <div className={`mt-1 text-xs ${t.featured ? "text-ivory/55" : "text-charcoal/50"}`}>
                    All-inclusive · {ac ? "AC villa" : "Non-AC villa"}
                  </div>
                </div>
                <a
                  href={buildWhatsAppLink("book")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`mt-6 inline-flex items-center justify-between gap-2 px-5 py-2.5 rounded-full text-sm font-medium ${
                    t.featured ? "bg-ivory text-charcoal" : "border border-sage/40 text-charcoal"
                  }`}
                >
                  Reserve
                  <ArrowRight size={15} />
                </a>
              </motion.article>
            ))}
          </div>

          {/* Desktop: full grid */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            {tiers.map((t, i) => (
              <motion.article
                key={t.days}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{
                  duration: 0.7,
                  delay: i * 0.06,
                  ease: [0.22, 1, 0.36, 1],
                }}
                whileHover={{
                  scale: 1.02,
                  y: -4,
                  transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] },
                }}
                className={`relative flex flex-col p-7 md:p-8 rounded-3xl border transition-shadow duration-500 ${
                  t.featured
                    ? "bg-charcoal text-ivory border-charcoal lg:scale-[1.03] shadow-[0_20px_60px_-20px_rgba(30,30,30,0.35)] hover:shadow-[0_28px_80px_-20px_rgba(30,30,30,0.55)]"
                    : "bg-white text-charcoal border-sage/15 hover:border-sage/40 shadow-[0_2px_10px_-4px_rgba(30,30,30,0.04)] hover:shadow-[0_22px_60px_-24px_rgba(30,30,30,0.18)]"
                }`}
                data-testid={`pricing-package-${t.days}`}
              >
                {t.featured && (
                  <span className="absolute -top-3 left-7 px-3 py-1 rounded-full bg-sage text-ivory text-[10px] uppercase tracking-[0.18em]">
                    Most Chosen
                  </span>
                )}
                <div className={`text-xs uppercase tracking-[0.2em] ${t.featured ? "text-ivory/65" : "text-sage"}`}>
                  {t.days} Days
                </div>
                <div className={`mt-3 font-serif text-2xl ${t.featured ? "text-ivory" : "text-charcoal"}`}>
                  {t.label}
                </div>
                <p className={`mt-2 text-sm leading-relaxed ${t.featured ? "text-ivory/70" : "text-charcoal/60"}`}>
                  {t.description}
                </p>

                <div className="mt-6">
                  <div
                    className={`font-serif text-4xl tracking-tight ${
                      t.featured ? "text-ivory" : "text-charcoal"
                    }`}
                  >
                    {ac ? t.ac : t.nonAc}
                  </div>
                  <div className={`mt-1 text-xs ${t.featured ? "text-ivory/55" : "text-charcoal/50"}`}>
                    All-inclusive · {ac ? "AC villa" : "Non-AC villa"}
                  </div>
                </div>

                <a
                  href={buildWhatsAppLink("book")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`mt-7 inline-flex items-center justify-between gap-2 px-5 py-3 rounded-full text-sm font-medium transition-colors ${
                    t.featured
                      ? "bg-ivory text-charcoal hover:bg-sage hover:text-ivory"
                      : "border border-sage/40 text-charcoal hover:bg-sage hover:text-ivory hover:border-sage"
                  }`}
                  data-testid={`pricing-cta-${t.days}`}
                >
                  Reserve {t.days} days
                  <ArrowRight size={15} />
                </a>
              </motion.article>
            ))}
          </div>
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-charcoal/55">
          <span className="inline-flex items-center gap-2">
            <Check size={14} className="text-sage" /> Bystander stay & meals included
          </span>
          <span className="inline-flex items-center gap-2">
            <Check size={14} className="text-sage" /> Senior caregivers, present never intrusive
          </span>
          <span className="inline-flex items-center gap-2">
            <Check size={14} className="text-sage" /> Doctor visits every 2 days
          </span>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
